import { useId, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FileEntry } from '@/lib/api/generated/model';
import { stemLength, validateEntryName } from '../folders';

/**
 * Gives one file or folder a new name where it stands.
 *
 * Modelled on `NewFolderDialog`: the name is checked as it is typed, from the
 * same rule the New folder dialog uses, so the two can never disagree about
 * what a name may be (FR-008). Two things are particular to renaming:
 *
 * - **The field opens on the current name, selected.** For a file, only the
 *   part before the extension is selected, so typing replaces the name and
 *   keeps the extension unless the reader deliberately changes it (FR-007).
 * - **An unchanged name is a cancel, not a request.** Confirming what was
 *   already there sends nothing (FR-010).
 *
 * The caller keys this component by the entry's path, so a different item
 * gets fresh state without an effect syncing props into it.
 */
export function RenameDialog({
  open,
  entry,
  existingNames,
  serverProblem,
  onRename,
  onCancel,
}: {
  open: boolean;
  entry: FileEntry | undefined;
  /** The siblings' names, without this entry's own. */
  existingNames: string[];
  /**
   * What the service said when it refused the last attempt — a name taken in
   * the meantime. Shown where a typed problem would be, until the reader edits
   * the name: the message was about a name they are no longer proposing.
   */
  serverProblem?: string;
  /** Only ever called with a valid, trimmed name that differs from the current one. */
  onRename: (newName: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(entry?.name ?? '');
  const [answeredProblem, setAnsweredProblem] = useState<string | undefined>(
    undefined,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedOnceRef = useRef(false);
  const problemId = useId();

  if (entry === undefined) return null;

  const kind = entry.kind;
  const trimmed = name.trim();
  const unchanged = trimmed === entry.name;
  const typedProblem = validateEntryName(kind, name, existingNames);
  const showServerProblem =
    serverProblem !== undefined && answeredProblem !== serverProblem;
  // Nothing typed yet is not a mistake, so an emptied field reads as empty
  // rather than as an error the reader has already made.
  const shownProblem = showServerProblem
    ? serverProblem
    : name !== '' && typedProblem !== undefined
      ? typedProblem
      : undefined;
  const canSubmit = typedProblem === undefined;

  function selectInitially(input: HTMLInputElement) {
    if (selectedOnceRef.current) return;
    selectedOnceRef.current = true;
    const end =
      kind === 'directory' ? input.value.length : stemLength(input.value);
    input.setSelectionRange(0, end);
  }

  function submit() {
    if (!canSubmit) return;
    if (unchanged) {
      onCancel();
      return;
    }
    onRename(trimmed);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {kind === 'directory' ? 'Rename folder' : 'Rename file'}
          </DialogTitle>
          <DialogDescription>
            {kind === 'directory'
              ? 'Everything inside keeps its place and its history; only the name changes.'
              : 'The file keeps its history; only the name changes.'}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Label htmlFor="rename-entry-name">Name</Label>
          <Input
            id="rename-entry-name"
            ref={inputRef}
            autoFocus
            value={name}
            aria-invalid={shownProblem !== undefined}
            aria-describedby={
              shownProblem !== undefined ? problemId : undefined
            }
            onFocus={(event) => {
              selectInitially(event.currentTarget);
            }}
            onChange={(event) => {
              setName(event.target.value);
              setAnsweredProblem(serverProblem);
            }}
          />
          {shownProblem !== undefined && (
            <p id={problemId} className="text-destructive text-sm">
              {shownProblem}
            </p>
          )}
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
